/* gmxGlow — derives a vibrant "glow" color from a card's image and stores it
   as the CSS custom property --glow on the card element. Pixels that are
   near-white, near-black, or low-saturation (gray) are ignored so the glow is
   always a real, colorful hue. Falls back silently (CSS keeps a default).

   IMPORTANT (Shopify): the sampling step draws the image into a <canvas> and
   reads it back with getImageData(). Theme and product images are served from
   cdn.shopify.com, a different origin, so without an explicit CORS opt-in the
   canvas is tainted and getImageData() throws — the glow silently never
   appears. Every image that wants a glow MUST carry crossorigin="anonymous"
   in the section/snippet markup that renders it. cdn.shopify.com returns
   Access-Control-Allow-Origin: *, so the anonymous request succeeds.
   Renderers that already do this: snippets/gmx-product-card.liquid.

   API (unchanged from the mockup):
     window.gmxGlow.attach(imgEl)          — compute on load, set --glow on the
                                             closest .glow-card ancestor
     window.gmxGlow.fromImageEl(card, img) — explicit card + image
     window.gmxGlow.fromColor(card, color) — set --glow directly
     window.gmxGlow.pickVibrant(imageData) — low-level hue picker

   This file also auto-attaches to any [data-gmx-glow] image on DOMContentLoaded
   so Liquid markup does not need inline JS. */
(function () {
  function rgb2hsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    var max = Math.max(r, g, b),
      min = Math.min(r, g, b),
      h = 0,
      s = 0,
      l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
    }
    return [h, s, l];
  }

  function hsl2rgb(h, s, l) {
    h /= 360;
    function hue(p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s,
      p = 2 * l - q;
    return [
      Math.round(hue(p, q, h + 1 / 3) * 255),
      Math.round(hue(p, q, h) * 255),
      Math.round(hue(p, q, h - 1 / 3) * 255),
    ];
  }

  /* Push a color to a vivid, glow-appropriate version of its own hue so the
     effect always reads as a colored glow (never a muddy/dark near-shadow). */
  function vivify(r, g, b) {
    var hsl = rgb2hsl(r, g, b);
    var rgb = hsl2rgb(hsl[0], Math.max(hsl[1], 0.62), Math.min(Math.max(hsl[2], 0.5), 0.6));
    return "rgb(" + rgb[0] + "," + rgb[1] + "," + rgb[2] + ")";
  }

  /* Bucket vibrant pixels by hue (12 buckets) and return the dominant hue's
     saturation-weighted average — avoids muddy results from averaging mixed hues. */
  function pickVibrant(data) {
    var buckets = {};
    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue;
      var r = data[i],
        g = data[i + 1],
        b = data[i + 2];
      var hsl = rgb2hsl(r, g, b),
        h = hsl[0],
        s = hsl[1],
        l = hsl[2];
      if (l < 0.18 || l > 0.85 || s < 0.3) continue; // skip black/white/gray
      var key = Math.floor(h / 30);
      var bk = buckets[key] || (buckets[key] = { r: 0, g: 0, b: 0, w: 0 });
      bk.r += r * s;
      bk.g += g * s;
      bk.b += b * s;
      bk.w += s;
    }
    var best = null,
      bestW = 0;
    for (var k in buckets) {
      if (buckets[k].w > bestW) {
        bestW = buckets[k].w;
        best = buckets[k];
      }
    }
    if (!best) return null;
    return vivify(best.r / best.w, best.g / best.w, best.b / best.w);
  }

  function fromImageEl(card, img) {
    if (!card || !img) return;
    try {
      var w = 28,
        h = 28;
      var cv = document.createElement("canvas");
      cv.width = w;
      cv.height = h;
      var ctx = cv.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var color = pickVibrant(ctx.getImageData(0, 0, w, h).data);
      if (color) card.style.setProperty("--glow", color);
    } catch (e) {
      /* tainted canvas / not loaded — keep default */
    }
  }

  function fromColor(card, color) {
    if (card && color) card.style.setProperty("--glow", color);
  }

  /* Ref-friendly: compute now if the image is already decoded (cached),
     otherwise wait for load. Safe to call with null. */
  function attach(img) {
    if (!img) return;
    var run = function () {
      fromImageEl(img.closest(".glow-card"), img);
    };
    if (img.complete && img.naturalWidth) run();
    else img.addEventListener("load", run, { once: true });
  }

  window.gmxGlow = { fromImageEl: fromImageEl, fromColor: fromColor, attach: attach, pickVibrant: pickVibrant };

  /* Declarative hook: <img data-gmx-glow crossorigin="anonymous"> inside a
     .glow-card gets a glow with no inline script. Runs once at DOM ready and
     again for markup injected later (call gmxGlow.scan()). */
  function scan(root) {
    var imgs = (root || document).querySelectorAll("img[data-gmx-glow]");
    for (var i = 0; i < imgs.length; i++) attach(imgs[i]);
  }
  window.gmxGlow.scan = scan;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      scan(document);
    });
  } else {
    scan(document);
  }
})();
